import { Injectable } from '@angular/core';
import { EnvironmentVariableService } from '../environment-variables/environment-variables';

declare global {
  interface Window {
    pendo: any;
  }
}

@Injectable({
  providedIn: 'root',
})
export class PendoService {
  private isInitialized = false;

  constructor() {}

  /**
   * Extract region from the current URL
   */
  private getRegionFromUrl(): string {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname.toLowerCase();
      if (hostname.includes('-us') || hostname.includes('us.')) {
        return 'US';
      } else if (hostname.includes('-emea') || hostname.includes('emea.')) {
        return 'EMEA';
      } else if (hostname.includes('-nz') || hostname.includes('nz.')) {
        return 'NZ';
      } else if (hostname.includes('-au') || hostname.includes('au.')) {
        return 'AU';
      }
    }
    return 'UNKNOWN';
  }

  /**
   * Extract session ID from JWT token stored in localStorage
   */
  private getSessionIdFromToken(): string | null {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const idToken = localStorage.getItem('id_token');
        if (idToken) {
          // Parse JWT token (format: header.payload.signature)
          const tokenParts = idToken.split('.');
          if (tokenParts.length === 3) {
            // Decode the payload (second part)
            const payload = JSON.parse(atob(tokenParts[1]));
            return payload.sid || null;
          }
        }
      }
    } catch (error) {
      console.warn('Failed to extract session ID from token:', error);
    }
    return null;
  }


  private getFeaturesList(): string[] {
    const idToken = localStorage.getItem('id_token');
    if (!idToken) return [];
    return JSON.parse(atob(idToken.split('.')[1])).features || [];
  }

  /**
   * Initialize Pendo with user and account data
   */
  initialize(user: any): void {
    if (typeof window !== 'undefined' && window.pendo) {
      try {
        // Extract region from URL
        const region = this.getRegionFromUrl();

        // Get environment from EnvironmentVariableService
        const environment = EnvironmentVariableService.getVar('ENVIRONMENT_NAME') || 'UNKNOWN';

        // Get session ID from JWT token
        const sessionId = this.getSessionIdFromToken();

        //Get Features from JWT token
        const features = this.getFeaturesList() || [];

        window.pendo.initialize({
          visitor: {
            id: user.userUid,
            email: user.email,
            full_name: user.name,
            first_name: user?.nameDetails?.firstName,
            last_name: user?.nameDetails?.lastName,
            roles: user.roles?.join(',') ?? '',
            language: user?.language || 'en',
            created_at: new Date(user?.createdDate).getTime(),
            features: features?.join(',') ?? '',

            region: region,
            environment: environment,
            hostname: window.location.hostname.toLowerCase(),
            session_id: sessionId
          },
          account: user.entity
            ? {
                id: user.entity?.entityUid,
                name: user.entity?.name,
                legalName: user.entity?.legalName,
                entityType: user.entity?.entityType,
                mcc: user.entity?.mcc,
                labels: user.entity?.labels?.join(',') ?? '',
                creationDate: new Date(user.entity?.createdDate).getTime(),
              }
            : undefined,
        });

        this.isInitialized = true;
        console.log('Pendo initialized successfully with metadata:', {
          region: region,
          environment: environment,
          session_id: sessionId
        });
      } catch (error) {
        console.error('Failed to initialize Pendo:', error);
      }
    } else {
      console.warn('Pendo is not loaded yet');
    }
  }

  /**
   * Track custom events
   */
  track(eventName: string, properties?: Record<string, any>): void {
    if (!this.isPendoReady()) return;

    try {
      window.pendo.track(eventName, properties);
      console.log(`Tracked event: ${eventName}`, properties);
    } catch (error) {
      console.error('Failed to track event in Pendo:', error);
    }
  }

  /**
   * Check if Pendo is ready
   */
  isReady(): boolean {
    return typeof window !== 'undefined' && !!window.pendo;
  }

  /**
   * Log user out (clear Pendo data)
   */
  logout(): void {
    if (!this.isPendoReady()) return;

    try {
      window.pendo.flushNow();
      this.isInitialized = false;
      console.log('User logged out from Pendo');
    } catch (error) {
      console.error('Failed to logout from Pendo:', error);
    }
  }

  private isPendoReady(): boolean {
    if (!this.isReady()) {
      console.warn('Pendo is not ready yet');
      return false;
    }
    return true;
  }
}
